/** Why `getCurrentPosition` came back empty — drives which toast copy to show. */
export type GeolocationFailure = 'unsupported' | 'permission_denied' | 'unavailable';

export interface GeolocationResult {
  readonly lat: number;
  readonly long: number;
}

type PositionOutcome =
  | { ok: true; value: GeolocationResult }
  | { ok: false; reason: GeolocationFailure };

/** One browser Geolocation request with the given options, promise-wrapped (never throws). */
function requestPosition(options: PositionOptions): Promise<PositionOutcome> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          ok: true,
          value: { lat: position.coords.latitude, long: position.coords.longitude },
        });
      },
      (error) => {
        // A Permissions-Policy block (server header `geolocation=()`) also reports
        // PERMISSION_DENIED — but the browser never prompted, so it's a site-config problem,
        // not a user choice. Classify it 'unavailable' so callers fall back to the IP-based
        // position instead of telling the user to change browser settings they never touched.
        // Browsers name the policy in the error message (Chromium: "…disabled in this document
        // by permissions policy", Firefox: "…disabled by Permissions Policy"); an unrecognized
        // message keeps the plain user-denial behavior.
        const policyBlocked =
          error.code === error.PERMISSION_DENIED &&
          /permissions? policy|feature policy/i.test(error.message);
        resolve({
          ok: false,
          reason:
            error.code === error.PERMISSION_DENIED && !policyBlocked
              ? 'permission_denied'
              : 'unavailable',
        });
      },
      options,
    );
  });
}

/** How long the best-accuracy sampling window watches for readings. */
const WATCH_WINDOW_MS = 8_000;
/** A reading at least this precise (meters) ends the sampling window early. */
const GOOD_ENOUGH_ACCURACY_M = 50;

/**
 * Watch briefly and keep the most ACCURATE reading — the first fix a browser reports is often a
 * coarse network guess that refines over the next seconds (a phone's GPS lock, a laptop's Wi-Fi
 * triangulation). Grabbing that first reading is how "use current location" pinned a point far
 * from the user while the mobile app (which waits on `bestForNavigation`) was correct. Resolves
 * with the best fix seen in the window, immediately once one is precise enough, or the error
 * outcome when nothing at all came in.
 */
function watchBestPosition(): Promise<PositionOutcome> {
  return new Promise((resolve) => {
    let best: GeolocationPosition | null = null;
    let settled = false;
    let watchId = 0;
    const finish = (outcome: PositionOutcome | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      navigator.geolocation.clearWatch(watchId);
      resolve(
        outcome ??
          (best
            ? { ok: true, value: { lat: best.coords.latitude, long: best.coords.longitude } }
            : { ok: false, reason: 'unavailable' }),
      );
    };
    const timer = setTimeout(() => finish(null), WATCH_WINDOW_MS);
    try {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (!best || position.coords.accuracy < best.coords.accuracy) best = position;
          if (position.coords.accuracy <= GOOD_ENOUGH_ACCURACY_M) finish(null);
        },
        (error) => {
          // Keep any reading already collected; report the failure only when we have nothing.
          if (best) return;
          const denied =
            error.code === error.PERMISSION_DENIED &&
            !/permissions? policy|feature policy/i.test(error.message);
          finish({ ok: false, reason: denied ? 'permission_denied' : 'unavailable' });
        },
        { enableHighAccuracy: true, timeout: WATCH_WINDOW_MS, maximumAge: 0 },
      );
    } catch (error) {
      // No Geolocation API here (jsdom, an insecure context): `watchPosition` throws synchronously
      // and the caller's catch takes the one-shot path — but the window timer was already armed
      // and used to fire 8s later, crashing on `navigator.geolocation.clearWatch` from inside a
      // timer where nothing could catch it. Disarm it before handing the throw on.
      clearTimeout(timer);
      throw error;
    }
  });
}

/**
 * Promise wrapper around the browser Geolocation API — the web equivalent of the Flutter
 * `Geolocator.getCurrentPosition(bestForNavigation)` call. Resolves a failure `reason` instead of
 * throwing, so callers can show the right message (mirrors the Flutter permission branches).
 *
 * Order of attempts:
 *  1. A best-accuracy sampling window (`watchBestPosition`) — waits for the fix to REFINE
 *     instead of pinning the first coarse network guess.
 *  2. A one-shot precise request, then a network-based retry accepting a recent cached fix —
 *     the web analog of Flutter's `getLastKnownPosition()` fallback (also the path taken in
 *     environments without `watchPosition`). A permission denial is always final.
 */
export async function getCurrentPosition(): Promise<PositionOutcome> {
  if (typeof navigator === 'undefined') {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const watched = await watchBestPosition();
    if (watched.ok || watched.reason === 'permission_denied') return watched;
  } catch {
    // watchPosition unavailable in this environment — the one-shot path below still works.
  }
  const precise = await requestPosition({ enableHighAccuracy: true, timeout: 10_000 });
  if (precise.ok || precise.reason === 'permission_denied') return precise;
  return requestPosition({ enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 });
}
